import { useState } from 'react';
import { data } from '../bridge/client';
import { t } from '../i18n/i18n';
import { Icon } from '../design/icons';

/** Login (+ MFA sub-state). Labels above fields, a 420 cap, no chrome — the native login's shape. */
export function Login({ onSignedIn }: { onSignedIn(userId: string): void }) {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfa, setMfa] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const r = await data.call('login', loginId.trim(), password, mfa ? code.replace(/\s+/g, '') : null).catch(() => ({ ok: false, error: 'network' as const }));
    setBusy(false);
    if (r.ok && 'userId' in r && r.userId) {
      onSignedIn(r.userId);
      return;
    }
    switch ('error' in r ? r.error : 'server') {
      case 'mfaRequired':
        setMfa(true);
        break;
      case 'mfaRejected':
        setError(t('login.error.mfaRejected'));
        break;
      case 'invalidCredentials':
        setError(t('login.error.invalidCredentials'));
        break;
      case 'rateLimited':
        setError(t('login.error.rateLimited'));
        break;
      case 'network':
        setError(t('login.error.network'));
        break;
      default:
        setError(t('login.error.server'));
    }
  };

  return (
    <main className="center-screen">
      <div className="wallpaper" />
      <form className="card" onSubmit={submit}>
        <div className="brand-mark">
          <Icon name="threads" size={26} strokeWidth={1.8} />
        </div>
        <h1>{mfa ? t('login.mfa.section') : t('login.title')}</h1>
        <p className="lede">{mfa ? t('login.mfa.instructions') : t('web.login.lede')}</p>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        {!mfa ? (
          <>
            <div className="field">
              <label htmlFor="login-id">{t('login.field.loginId')}</label>
              <input id="login-id" autoComplete="username" autoFocus value={loginId} onChange={(e) => setLoginId(e.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="password">{t('login.field.password')}</label>
              <input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
          </>
        ) : (
          <div className="field">
            <label htmlFor="mfa">{t('login.mfa.field.code')}</label>
            <input id="mfa" inputMode="numeric" autoComplete="one-time-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} required />
          </div>
        )}
        <button className="primary-button" type="submit" disabled={busy}>
          {mfa ? t('login.mfa.button.verify') : t('login.button.submit')}
        </button>
        {mfa ? (
          <button
            type="button"
            className="secondary-button"
            style={{ width: '100%', marginTop: 8 }}
            onClick={() => {
              setMfa(false);
              setCode('');
              setError(null);
            }}
          >
            {t('login.mfa.button.cancel')}
          </button>
        ) : null}
      </form>
    </main>
  );
}
